import { keepPreviousData, type QueryClient, queryOptions } from '@tanstack/react-query';

import { apiAdminViewsFetch, handleApiResponseErrorWithoutToast } from '..';
import type {
  DealBoardDto,
  DealBoardEntryDetailDto,
  DealBoardFilterOptionsDto,
  InvestmentBoardDto,
  InvestmentBoardEntryDetailDto,
  OfferDealsResponseDto,
  OfferOpenEntryCountDto,
  PersonDealsResponseDto,
} from './types';

export const PIPELINE_QUERY_KEYS = {
  board: 'pipeline-board',
  boardFilterOptions: 'pipeline-board-filter-options',
  cardDetail: 'pipeline-card-detail',
  offerOpenEntryCount: 'pipeline-offer-open-entry-count',
  personDeals: 'pipeline-person-deals',
  offerDeals: 'pipeline-offer-deals',
} as const;

type DealPipelineBoardFilters = {
  investorUserIds?: string[];
  companyIds?: string[];
  providerIds?: string[];
  stages?: string[];
  activeOnly?: boolean;
};

type InvestmentPipelineBoardFilters = {
  introducerUserIds?: string[];
  includeNoIntroducer?: boolean;
  investorUserIds?: string[];
  fundIds?: string[];
  introductionTypes?: string[];
  stages?: string[];
  timerStatuses?: string[];
  activeOnly?: boolean;
  sort?: string;
};

const INVESTMENT_PERSPECTIVE_QUERY = { perspective: 'gamma' } as const;

export const buildDealPipelineBoardQueryParams = (filters: DealPipelineBoardFilters = {}) =>
  removeEmptyValues({
    userIds: sortArray(filters.investorUserIds),
    companyIds: sortArray(filters.companyIds),
    providerIds: sortArray(filters.providerIds),
    stages: sortArray(filters.stages),
    activeOnly: filters.activeOnly,
  });

export const buildInvestmentPipelineBoardQueryParams = (
  filters: InvestmentPipelineBoardFilters = {},
) =>
  removeEmptyValues({
    ...INVESTMENT_PERSPECTIVE_QUERY,
    introducerUserIds: sortArray(filters.introducerUserIds),
    includeNoIntroducer: filters.includeNoIntroducer,
    investorUserIds: sortArray(filters.investorUserIds),
    fundIds: sortArray(filters.fundIds),
    introductionTypes: sortArray(filters.introductionTypes),
    stages: sortArray(filters.stages),
    timerStatuses: sortArray(filters.timerStatuses),
    activeOnly: filters.activeOnly,
    sort: filters.sort,
  });

/**
 * The board honours every filter server-side, so the response also decides which
 * stage columns exist: `stages` narrows the returned columns to the selection and
 * `activeOnly` drops the terminal ones.
 *
 * `keepPreviousData` is what lets the previously loaded board stay on screen
 * while a newly filtered one is in flight.
 */
export const _getDealPipelineBoard = (filters?: DealPipelineBoardFilters) => {
  const query = buildDealPipelineBoardQueryParams(filters);

  return queryOptions({
    queryKey: [PIPELINE_QUERY_KEYS.board, 'deals', query],
    queryFn: ({ signal }) => apiAdminViewsFetch<DealBoardDto>('/deal-board', { query, signal }),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    // Returning through client-side navigation must reconcile optimistic board
    // state with the server even while the cached response is still fresh.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
};

/**
 * The live card for a buyer × offer pair, looked up in cached deal boards. BFF errors carry no
 * error data, so a 409 on manual creation cannot name the existing card. A buyer may be assigned
 * under more than one id (a registered contact's card belongs to their platform user), so any of
 * `buyerIds` matches. Require membership in the visible board as well: a card hidden by filters
 * cannot be opened from there.
 */
export const findCachedDealBoardEntryId = (
  queryClient: QueryClient,
  { offerId, buyerIds }: { offerId: string; buyerIds: readonly string[] },
  visibleEntries: readonly {
    id: string;
    stage: string;
    cancellationOutcome?: string | null;
  }[],
): string | null => {
  const visibleById = new Map(visibleEntries.map((entry) => [entry.id, entry]));
  if (visibleById.size === 0) return null;

  for (const [, board] of queryClient.getQueriesData<DealBoardDto>({
    queryKey: [PIPELINE_QUERY_KEYS.board, 'deals'],
  })) {
    for (const column of board?.columns ?? []) {
      for (const candidate of column.entries) {
        const visible = visibleById.get(candidate.id);
        if (
          visible &&
          !(visible.stage === 'DealCanceled' && visible.cancellationOutcome === 'NotInterested') &&
          candidate.offer.id === offerId &&
          buyerIds.includes(candidate.user.id)
        ) {
          return candidate.id;
        }
      }
    }
  }
  return null;
};

/**
 * Filter options for the deal board. The endpoint takes no query, so the lists
 * cover every company, provider and investor on the board — including ones the
 * current filters exclude. Kept out of the board query so changing a filter does
 * not refetch the dropdown contents alongside it.
 */
export const _getDealPipelineBoardFilterOptions = () =>
  queryOptions({
    queryKey: [PIPELINE_QUERY_KEYS.boardFilterOptions, 'deals'],
    queryFn: ({ signal }) =>
      apiAdminViewsFetch<DealBoardFilterOptionsDto>('/deal-board/filter-options', { signal }),
    staleTime: 5 * 60 * 1000,
  });

/**
 * Live Pipeline cards on an offer that a delete would move to "Offer deleted" (GP-4681).
 * Admin only, like the delete itself.
 */
export const _getOfferOpenEntryCount = (offerId?: string | null) =>
  queryOptions({
    queryKey: [PIPELINE_QUERY_KEYS.offerOpenEntryCount, offerId],
    queryFn: ({ signal }) =>
      apiAdminViewsFetch<OfferOpenEntryCountDto>(
        `/deal-board/offers/${offerId ?? ''}/open-entry-count`,
        // A failed count must not toast: the delete dialog still opens and warns without it.
        { signal, onResponseError: handleApiResponseErrorWithoutToast },
      ),
    enabled: Boolean(offerId),
    staleTime: 0,
    retry: false,
  });

export const _getInvestmentPipelineBoard = (filters?: InvestmentPipelineBoardFilters) => {
  const query = buildInvestmentPipelineBoardQueryParams(filters);

  return queryOptions({
    queryKey: [PIPELINE_QUERY_KEYS.board, 'investment', query],
    queryFn: ({ signal }) =>
      apiAdminViewsFetch<InvestmentBoardDto>('/investment-board', { query, signal }),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const _getDealPipelineCardDetail = (cardId?: string | null) =>
  queryOptions({
    queryKey: [PIPELINE_QUERY_KEYS.cardDetail, 'deals', cardId],
    queryFn: ({ signal }) =>
      apiAdminViewsFetch<DealBoardEntryDetailDto>(`/deal-board/entries/${cardId ?? ''}`, {
        signal,
      }),
    enabled: Boolean(cardId),
    staleTime: 30 * 1000,
  });

export const _getInvestmentPipelineCardDetail = (cardId?: string | null) =>
  queryOptions({
    queryKey: [PIPELINE_QUERY_KEYS.cardDetail, 'investment', cardId, INVESTMENT_PERSPECTIVE_QUERY],
    queryFn: ({ signal }) =>
      apiAdminViewsFetch<InvestmentBoardEntryDetailDto>(
        `/investment-board/entries/${cardId ?? ''}`,
        {
          query: INVESTMENT_PERSPECTIVE_QUERY,
          signal,
        },
      ),
    enabled: Boolean(cardId),
    staleTime: 30 * 1000,
  });

export const _getPersonDeals = (userId: string) =>
  queryOptions({
    queryKey: [PIPELINE_QUERY_KEYS.personDeals, userId],
    queryFn: ({ signal }) =>
      apiAdminViewsFetch<PersonDealsResponseDto>(`/users/${userId}/deals`, { signal }),
    refetchOnMount: 'always',
  });

export const _getOfferDeals = (offerId: string) =>
  queryOptions({
    queryKey: [PIPELINE_QUERY_KEYS.offerDeals, offerId],
    queryFn: ({ signal }) =>
      apiAdminViewsFetch<OfferDealsResponseDto>(
        `/deal-board/offers/${encodeURIComponent(offerId)}/deals`,
        {
          signal,
        },
      ),
    staleTime: 0,
    refetchOnMount: 'always',
  });

const sortArray = <T extends string>(value?: readonly T[]): T[] => (value ? [...value].sort() : []);

// Keep React Query keys and backend query params stable for equivalent filters.
// Empty arrays and false optional toggles mean "not applied" for these endpoints.
const removeEmptyValues = <T extends Record<string, unknown>>(value: T) =>
  Object.fromEntries(
    Object.entries(value).filter(([, item]) => {
      if (Array.isArray(item)) return item.length > 0;
      if (item === undefined || item === null || item === false) return false;
      return item !== '';
    }),
  ) as Partial<T>;
